// src/pages/Report/components/SearchableSelectFilter.js

import React, { useState, useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const SearchableSelectFilter = ({
  label,
  opciones = [],
  value = [],
  onChange,
  disabled = false,
  sx = {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) return value.map(String);
    if (value !== undefined && value !== null && value !== "") return [String(value)];
    return [];
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return opciones;
    const term = searchTerm.toLowerCase();
    return opciones.filter((op) =>
      String(op.descripcion || "").toLowerCase().includes(term)
    );
  }, [opciones, searchTerm]);

  const totalOptions = opciones.length;
  const isAllSelected = totalOptions > 0 && selectedValues.length === totalOptions;
  const isSomeSelected = selectedValues.length > 0 && selectedValues.length < totalOptions;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      if (searchTerm.trim() && filteredOptions.length < totalOptions) {
        // Si hay búsqueda activa, agregar los filtrados a la selección
        const filteredValStrings = filteredOptions.map((o) => String(o.value));
        const combined = Array.from(new Set([...selectedValues, ...filteredValStrings]));
        onChange(combined);
      } else {
        onChange(opciones.map((o) => o.value));
      }
    }
  };

  const handleToggleOption = (opVal) => {
    const strVal = String(opVal);
    if (selectedValues.includes(strVal)) {
      onChange(selectedValues.filter((v) => v !== strVal));
    } else {
      onChange([...selectedValues, opVal]);
    }
  };

  const selectedTooltipText = useMemo(() => {
    if (selectedValues.length === 0) return "";
    return selectedValues
      .map((v) => opciones.find((o) => String(o.value) === v)?.descripcion || v)
      .join("\n");
  }, [selectedValues, opciones]);

  return (
    <FormControl
      size="small"
      disabled={disabled}
      sx={{
        width: 260,
        maxWidth: 320,
        ...sx,
      }}
    >
      <InputLabel sx={{ fontSize: "var(--app-font-size)" }}>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={selectedValues}
        onClose={() => setSearchTerm("")}
        renderValue={() => {
          let text = "";
          if (selectedValues.length === 0) {
            return <span style={{ color: "#94a3b8" }}>-- Seleccione --</span>;
          } else if (isAllSelected) {
            text = `Todos (${totalOptions})`;
          } else if (selectedValues.length === 1) {
            const found = opciones.find((o) => String(o.value) === selectedValues[0]);
            text = found ? found.descripcion : selectedValues[0];
          } else {
            text = `${selectedValues.length} seleccionados`;
          }

          return (
            <span
              title={selectedTooltipText}
              style={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </span>
          );
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 380,
              minWidth: 280,
              maxWidth: 450,
              overflowX: "hidden",
            },
          },
        }}
        sx={{
          fontSize: "var(--app-font-size)",
          height: "calc(var(--app-font-size) * 2.5)",
          "& .MuiSelect-select": {
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            py: 0.5,
            pr: "32px !important",
          },
        }}
      >
        {/* Cabecera pegajosa con el Buscador */}
        <Box
          sx={{
            p: 1,
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 10,
            borderBottom: "1px solid #e2e8f0",
          }}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={`Buscar ${label.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm("");
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "var(--app-font-size)",
                py: 0.5,
              },
            }}
          />
        </Box>

        {/* Opción Seleccionar Todo */}
        <MenuItem
          dense
          onClick={(e) => {
            e.stopPropagation();
            handleSelectAll();
          }}
          sx={{
            fontWeight: 600,
            fontSize: "var(--app-font-size)",
            borderBottom: "1px solid #e2e8f0",
            py: 0.5,
            bgcolor: isAllSelected ? "action.hover" : "inherit",
          }}
        >
          <Checkbox
            size="small"
            checked={isAllSelected}
            indeterminate={isSomeSelected && !isAllSelected}
            sx={{ p: 0.5 }}
          />
          <ListItemText
            primary={
              searchTerm.trim()
                ? `Seleccionar filtrados (${filteredOptions.length})`
                : `Seleccionar todo (${totalOptions})`
            }
            primaryTypographyProps={{
              fontSize: "var(--app-font-size)",
              fontWeight: 600,
            }}
          />
        </MenuItem>

        {/* Lista de Opciones Filtradas */}
        {filteredOptions.length === 0 ? (
          <MenuItem disabled sx={{ fontSize: "var(--app-font-size)", fontStyle: "italic", py: 1.5, justifyContent: "center" }}>
            No se encontraron resultados
          </MenuItem>
        ) : (
          filteredOptions.map((op) => {
            const isChecked = selectedValues.includes(String(op.value));
            return (
              <MenuItem
                dense
                key={op.value}
                value={op.value}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleOption(op.value);
                }}
                sx={{ fontSize: "var(--app-font-size)", py: 0.5 }}
              >
                <Checkbox size="small" checked={isChecked} sx={{ p: 0.5 }} />
                <ListItemText
                  primary={op.descripcion}
                  primaryTypographyProps={{
                    fontSize: "var(--app-font-size)",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                />
              </MenuItem>
            );
          })
        )}
      </Select>
    </FormControl>
  );
};

export default SearchableSelectFilter;

